import "../index.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useState, useEffect, useCallback, useRef, cloneElement } from "react";
import ApiCall from "../auth/ApiCall";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import { useParams, useNavigate } from "react-router-dom";
import { AccessVerification, ProAccessCheck } from "../auth/UserProfile";

interface PropBet {
  player_name: string;
  prop_type: string;
  line: number | string;
  over_odds: number | string;
  under_odds: number | string;
  [key: string]: unknown;
}

// response["event"]["odds"][sportsbook] => { field: value, ... }  (2-level)
type OddsEntry = Record<string, unknown>;
type SimpleOddsMap = Record<string, OddsEntry>;

// response["event"]["game_odds" | "player_odds"][sportsbook][odds_type] => { field: value, ... }  (3-level)
type SportsbookOdds = Record<string, OddsEntry>;
type GameOddsMap = Record<string, SportsbookOdds>;

interface EventData {
  odds?: SimpleOddsMap;
  game_odds?: GameOddsMap;
  player_odds?: GameOddsMap;
  // Pre-game snapshots — the live maps above are empty right after a game's
  // start time passes but before the live feed has produced data yet.
  starting_odds?: SimpleOddsMap;
  starting_game_odds?: GameOddsMap;
  starting_player_odds?: GameOddsMap;
  home_team?: string;
  away_team?: string;
  sport?: string;
  home_score?: number;
  away_score?: number;
  start_time?: string | number;
  game_status?: Record<string, unknown>;
  [key: string]: unknown;
}

interface PropsData {
  event?: EventData;
  game_id?: number;
  home_team?: string;
  away_team?: string;
  sport?: string;
  props?: PropBet[];
  [key: string]: unknown;
}

// A rule that has hit for this game. Keyed in the API response by game id
// (matching the "triggers" shape used on the active_games page); for a
// team/game-level prop, prop_key names the raw game_odds field. For a player
// prop, player_id + prop_stem identify the player and the stat field instead.
interface PropTrigger {
  title: string;
  details: string;
  rule_id: number;
  rule_definition_id: number;
  prop_key: string | null;
  player_id: string | null;
  prop_stem: string | null;
}

// ── Trigger alert banner ─────────────────────────────────────────────────────
// Shown inline on a matching prop row/section, cycling through multiple hits
// the same way the active_games page's Game.tsx does.

function TriggerAlert({ triggers }: { triggers: PropTrigger[] }) {
  const [index, setIndex] = useState(0);
  if (triggers.length === 0) return null;
  const current = triggers[index] ?? triggers[0];

  return (
    <div
      className="font_family_base"
      style={{
        borderTop: "2px solid #007bff",
        borderLeft: "2px solid #007bff",
        borderRight: "2px solid #007bff",
        borderBottom: "2px solid #00bf62",
        borderRadius: 10,
        padding: "8px 12px",
        margin: "8px 10px",
      }}
    >
      <div style={{ textAlign: "center", fontSize: 12, color: "#ccc" }}>
        {current.title}
      </div>
      <div
        style={{
          whiteSpace: "pre-line",
          marginTop: 4,
          fontSize: 12,
          color: "#ccc",
        }}
        dangerouslySetInnerHTML={{ __html: current.details }}
      />
      {triggers.length > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 6,
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIndex((prev) => (prev > 0 ? prev - 1 : triggers.length - 1));
            }}
            style={{
              background: "transparent",
              border: "1px solid #ccc",
              color: "#ccc",
              cursor: "pointer",
              padding: "3px 8px",
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            ←
          </button>
          <span style={{ fontSize: 10, color: "#999" }}>
            {index + 1} / {triggers.length}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIndex((prev) => (prev < triggers.length - 1 ? prev + 1 : 0));
            }}
            style={{
              background: "transparent",
              border: "1px solid #ccc",
              color: "#ccc",
              cursor: "pointer",
              padding: "3px 8px",
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Collapsible section ──────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
  indent = false,
  onChartClick,
  highlighted = false,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  indent?: boolean;
  onChartClick?: () => void;
  highlighted?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        marginBottom: indent ? 8 : 16,
        borderRadius: 12,
        overflow: "hidden",
        border: highlighted ? "2px solid #ff0000" : "1px solid #333",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="font_family_base"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: indent ? "8px 14px" : "12px 16px",
          background: indent ? "#1e1e1e" : "#252525",
          border: "none",
          cursor: "pointer",
          color: "#fff",
          fontSize: indent ? 14 : 16,
          fontWeight: indent ? 500 : 700,
          textAlign: "left",
        }}
      >
        <span>{title}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {onChartClick && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChartClick();
              }}
              style={{ fontSize: 16, cursor: "pointer", lineHeight: 1 }}
            >
              📊
            </span>
          )}
          <span style={{ color: "#9ca3af", fontSize: 12 }}>
            {open ? "▲" : "▼"}
          </span>
        </div>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatOdds(val: unknown): string {
  if (val === null || val === undefined || val === "") return "-";
  // An array/object (e.g. an unhandled "_lines" alt-line list) has no
  // sensible single-value display — stringifying it would print
  // "[object Object]" per entry instead of failing quietly.
  if (typeof val === "object") return "-";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return n > 0 ? `+${n}` : String(n);
}

import {
  toTitleLabel,
  COMPOUND_GROUPS,
  BASE_TO_COMPOUND,
  PREFIX_GROUPS_DISPLAY,
  type CompoundMember,
} from "./gamePropsConfig";

const ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "50% 25% 25%",
  alignItems: "center",
  padding: "8px 14px",
  fontSize: 13,
} as const;

const HIDDEN_ODDS_TYPES = new Set([
  "regulation_spread",
  "reg_away_has_points",
  "reg_away_no_points",
  "reg_both_has_scores",
  "reg_both_no_scores",
  "reg_home_has_points",
  "reg_home_no_points",
]);

const HIDDEN_ODDS_PREFIXES = ["match_result_btts"];

const GAME_ODDS_LABEL_OVERRIDES: Record<string, string> = {};

// Sport-specific display renames for Game Odds sections, keyed by the section's
// type (compound group key, or the raw base for ungrouped types).
type LabelOverrides = Record<string, string>;

const MLS_LABEL_OVERRIDES: LabelOverrides = {
  over_total_shots_on_target: "Total Shots On Target",
  reg_points_even_odd: "Points Odd/Even",
  reg_has_points: "Regulation Has Points",
  reg_no_points: "Regulation No Points",
  away_corner_kicks_line: "Team Corners",
};

// Per-book member selection for a compound group: given a book's odds and the
// group's members, returns the members that apply to that book (default: all).
// Lets two groups split one set of ambiguous keys by what each book offers.
type MemberResolvers = Record<
  string,
  (sbOdds: OddsEntry, members: CompoundMember[]) => CompoundMember[]
>;

const hasFirstHalfDraw = (sbOdds: OddsEntry) =>
  Object.keys(sbOdds).some((k) => k.startsWith("first_half_draw_outcome"));

// NFL/NCAAF 1st-half moneylines come in both 2-way and 3-way flavors, and books
// reuse "first_half_away/home_outcome" for both. Split by book: a book that
// offers a draw price is 3-way; a book without one is 2-way (its away/home
// outcome keys join the 2-way group alongside "away/home_first_half_outcome").
const FOOTBALL_MEMBER_RESOLVERS: MemberResolvers = {
  h1_moneyline: (sbOdds, members) => (hasFirstHalfDraw(sbOdds) ? members : []),
  h1_moneyline_alt: (sbOdds, members) =>
    hasFirstHalfDraw(sbOdds)
      ? members
      : [
          ...members,
          { base: "first_half_away_outcome", rowLabel: "Away" },
          { base: "first_half_home_outcome", rowLabel: "Home" },
        ],
};

const FOOTBALL_LABEL_OVERRIDES: LabelOverrides = {
  h1_moneyline: "Moneyline 3-way",
  h1_moneyline_alt: "Moneyline 2-way",
};

function getOddsTypeDisplayLabel(
  t: string,
  overrides?: LabelOverrides,
): string {
  if (overrides?.[t]) return overrides[t];
  if (GAME_ODDS_LABEL_OVERRIDES[t]) return GAME_ODDS_LABEL_OVERRIDES[t];
  for (const { prefix, label } of Object.values(PREFIX_GROUPS_DISPLAY)) {
    if (t.startsWith(prefix)) return label;
  }
  if (COMPOUND_GROUPS[t]) return COMPOUND_GROUPS[t].label;
  return toTitleLabel(t);
}

// Same base→compound-group normalization collectTypes uses, exposed so a
// trigger's raw prop_key can be resolved to the "oddsType" a section is keyed by.
function normalizeToOddsType(key: string): string {
  const base = key.replace(/_point$|_price$|_lines$/, "");
  return BASE_TO_COMPOUND[base] ?? base;
}

// Sport-specific merges of one compound group into another (source → target):
// the source group's members are displayed as extra members of the target's
// section, and the source no longer gets a section of its own.
type MergeTypes = Record<string, string>;

// MLB reports total runs under both "total_points_even/odd" and
// "total_runs_odd/even" depending on the book — show them as one market.
const MLB_MERGE_TYPES: MergeTypes = {
  total_points_even: "total_runs_even_odd",
};

function triggersForOddsType(
  triggers: PropTrigger[],
  oddsType: string,
  mergeTypes?: MergeTypes,
): PropTrigger[] {
  return triggers.filter((t) => {
    if (t.player_id || !t.prop_key) return false;
    const type = normalizeToOddsType(t.prop_key);
    return (mergeTypes?.[type] ?? type) === oddsType;
  });
}

function collectTypes(oddsMap: GameOddsMap, mergeTypes?: MergeTypes): string[] {
  const seen = new Set<string>();
  const types: string[] = [];
  Object.values(oddsMap).forEach((sbOdds) => {
    Object.keys(sbOdds).forEach((field) => {
      // Strip _point, _price, or _lines to get the base type key
      const base = field.replace(/_point$|_price$|_lines$/, "");
      // Map to compound group key if applicable, otherwise use the base
      const compoundKey = BASE_TO_COMPOUND[base] ?? base;
      const key = mergeTypes?.[compoundKey] ?? compoundKey;
      const hidden =
        HIDDEN_ODDS_TYPES.has(key) ||
        HIDDEN_ODDS_PREFIXES.some((prefix) => key.startsWith(prefix));
      if (!seen.has(key) && !hidden) {
        seen.add(key);
        types.push(key);
      }
    });
  });
  return types;
}

// Processes a flat odds entry into paired/labelled display fields.
function processOddsEntry(entry: OddsEntry): SubgroupField[] {
  const processed = new Set<string>();
  const results: SubgroupField[] = [];

  Object.keys(entry).forEach((f) => {
    if (processed.has(f)) return;

    if (f.endsWith("_point") || f.endsWith("_price")) {
      const base = f.replace(/_point$|_price$/, "");
      const pointKey = `${base}_point`;
      const priceKey = `${base}_price`;
      processed.add(pointKey);
      processed.add(priceKey);

      const parts: string[] = [];
      const p = formatPoint(entry[pointKey]);
      if (p) parts.push(p);
      const pr = formatOdds(entry[priceKey]);
      if (pr !== "-") parts.push(`(${pr})`);
      if (parts.length === 0) return;

      const label = toTitleLabel(
        base
          .replace(/_total$/, "")
          .replace(/_spread$/, "")
          .replace(/_handicap$/, "")
          .replace(/_line$/, ""),
      );
      results.push({ label, display: parts.join(" "), key: base });
    } else if (f.toLowerCase().includes("outcome")) {
      processed.add(f);
      const val = entry[f];
      if (val === null || val === undefined || val === "") return;
      results.push({
        label: toTitleLabel(f.split("_outcome")[0]),
        display: formatOdds(val),
        key: f,
      });
    } else {
      processed.add(f);
      const val = entry[f];
      if (val === null || val === undefined || val === "") return;
      results.push({
        label: toTitleLabel(f),
        display: formatOdds(val),
        key: f,
      });
    }
  });

  return results;
}

// The chart page fetches by exact key (e.g. "away_spread_point"), so a bare
// member/group base like "away_spread" isn't itself fetchable — it needs
// whichever suffix the live data actually uses appended. Guessing a fixed
// suffix broke down for groups that only have "_price" (or, rarely, no
// suffix at all), silently redirecting the chart to an unrelated default key.
function suffixedKeyIfPresent(base: string, sbOdds: OddsEntry): string | null {
  if (`${base}_point` in sbOdds) return `${base}_point`;
  if (`${base}_price` in sbOdds) return `${base}_price`;
  if (base in sbOdds) return base;
  return null;
}

function resolveSuffixedKey(
  base: string,
  sbOdds: OddsEntry | undefined,
): string {
  return (sbOdds && suffixedKeyIfPresent(base, sbOdds)) || `${base}_point`;
}

// Picks whichever compound-group member actually has data (preferring the
// "Over" member, then declaration order) — the "books" list for a compound
// group is filtered by ANY member having data, so the preferred member (e.g.
// the first one, or "Over") isn't guaranteed to be the one that's actually
// present. Falling back to a member with no real data anywhere reproduced
// the same "jumps to an unrelated key" bug this was meant to fix.
function resolveCompoundChartKey(
  members: CompoundMember[],
  books: [string, OddsEntry][],
): string {
  const overMember = members.find((m) => m.rowLabel === "Over");
  const ordered = overMember
    ? [overMember, ...members.filter((m) => m !== overMember)]
    : members;
  for (const member of ordered) {
    for (const [, sbOdds] of books) {
      const found = suffixedKeyIfPresent(member.base, sbOdds);
      if (found) return found;
    }
  }
  return `${ordered[0].base}_point`;
}

// ── Odds type sub-section (used by Game Odds and Player Odds) ─────────────────

function OddsTypeSection({
  oddsType,
  oddsMap,
  onChartClick,
  displayLabel,
  triggers = [],
  mergeTypes,
  labelOverrides,
  memberResolvers,
}: {
  oddsType: string;
  oddsMap: GameOddsMap;
  onChartClick?: (key: string) => void;
  displayLabel?: string;
  triggers?: PropTrigger[];
  mergeTypes?: MergeTypes;
  labelOverrides?: LabelOverrides;
  memberResolvers?: MemberResolvers;
}) {
  const baseCompound = COMPOUND_GROUPS[oddsType];
  const compound = baseCompound && {
    ...baseCompound,
    members: [
      ...baseCompound.members,
      ...Object.entries(mergeTypes ?? {})
        .filter(([, target]) => target === oddsType)
        .flatMap(([source]) => COMPOUND_GROUPS[source]?.members ?? []),
    ],
  };
  const matchedTriggers = triggersForOddsType(triggers, oddsType, mergeTypes);

  if (compound) {
    const resolveMembers = memberResolvers?.[oddsType];
    const membersFor = (sbOdds: OddsEntry): CompoundMember[] =>
      resolveMembers ? resolveMembers(sbOdds, compound.members) : compound.members;
    const books = Object.entries(oddsMap).filter(([, sbOdds]) =>
      membersFor(sbOdds).some(
        ({ base }) =>
          `${base}_point` in sbOdds ||
          `${base}_price` in sbOdds ||
          `${base}_lines` in sbOdds ||
          base in sbOdds,
      ),
    );
    if (books.length === 0) return null;

    // Union of the members that applied to each book, so a book-specific
    // member (see MemberResolvers) can still supply the chart key.
    const chartMembers = [
      ...new Map(
        books.flatMap(([, sbOdds]) =>
          membersFor(sbOdds).map((m) => [m.base, m] as const),
        ),
      ).values(),
    ];
    const chartKey = resolveCompoundChartKey(chartMembers, books);

    // Book keys can be present without any actually-displayable value (e.g.
    // a future inning's fields exist but are all null) — compute the real
    // rows up front so an all-empty market can bail out to null instead of
    // rendering an empty accordion.
    const renderedBooks = books
      .map(([book, sbOdds]) => {
        const rows: SubgroupField[] = [];
        membersFor(sbOdds).forEach(({ base, rowLabel }) => {
          const linesVal = sbOdds[`${base}_lines`];
          const pointVal = sbOdds[`${base}_point`];
          const priceVal = sbOdds[`${base}_price`];
          // A _lines array with a flagged main line wins outright; one
          // missing a flagged main gets the flat point/price merged in
          // as the main line instead (see resolveLineFields).
          const resolvedLines = resolveLineFields(
            linesVal,
            pointVal,
            priceVal,
            rowLabel,
            base,
          );
          if (resolvedLines !== null) {
            rows.push(...resolvedLines);
            return;
          }
          const directVal = sbOdds[base];
          if (
            pointVal === undefined &&
            priceVal === undefined &&
            directVal === undefined
          )
            return;
          let display: string;
          if (pointVal !== undefined || priceVal !== undefined) {
            const parts: string[] = [];
            const p = formatPoint(pointVal);
            if (p) parts.push(p);
            const pr = formatOdds(priceVal);
            if (pr !== "-") parts.push(pointVal !== undefined ? `(${pr})` : pr);
            display = parts.join(" ");
          } else {
            display = formatOdds(directVal);
          }
          if (display && display !== "-")
            rows.push({ label: rowLabel, display, key: base });
        });
        const seenLabels = new Set<string>();
        const dedupedRows = rows.filter((row) => {
          if (row.isAlt) return true; // always keep alt lines
          if (seenLabels.has(row.label)) return false;
          seenLabels.add(row.label);
          return true;
        });
        return dedupedRows.length > 0 ? { book, dedupedRows } : null;
      })
      .filter(
        (b): b is { book: string; dedupedRows: SubgroupField[] } => b !== null,
      );

    if (renderedBooks.length === 0) return null;

    return (
      <CollapsibleSection
        title={labelOverrides?.[oddsType] ?? compound.label}
        indent
        onChartClick={onChartClick ? () => onChartClick(chartKey) : undefined}
        highlighted={matchedTriggers.length > 0}
      >
        <TriggerAlert triggers={matchedTriggers} />
        <div style={{ background: "#000" }}>
          {renderedBooks.map(({ book, dedupedRows }, bi) => (
            <div
              key={book}
              style={{
                borderBottom:
                  bi < renderedBooks.length - 1 ? "1px solid #3a3a3a" : "none",
              }}
            >
              {dedupedRows.map((row, ri) => (
                <div
                  key={row.key}
                  className="font_family_base"
                  style={ROW_STYLE}
                >
                  <span style={{ fontWeight: 600, color: "#d1d5db" }}>
                    {ri === 0 ? book : ""}
                  </span>
                  <span
                    style={{
                      color: row.isAlt ? "#a0a0a0" : "#9ca3af",
                      textAlign: "center",
                    }}
                  >
                    {row.label}
                  </span>
                  <span
                    style={{
                      color: row.isAlt ? "#e0e0e0" : "#fff",
                      textAlign: "right",
                    }}
                  >
                    {row.display}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </CollapsibleSection>
    );
  }

  const books = Object.entries(oddsMap).filter(
    ([, sbOdds]) =>
      oddsType in sbOdds ||
      `${oddsType}_point` in sbOdds ||
      `${oddsType}_price` in sbOdds,
  );
  if (books.length === 0) return null;
  const chartKey = resolveSuffixedKey(oddsType, books[0]?.[1]);

  // Book keys can be present without any actually-displayable value (e.g. a
  // future inning's fields exist but are all null) — compute the real fields
  // up front so an all-empty market can bail out to null instead of
  // rendering an empty accordion.
  const renderedBooks = books
    .map(([book, sbOdds]) => {
      const rawVal = sbOdds[oddsType];
      let fields: SubgroupField[];

      if (
        rawVal !== null &&
        rawVal !== undefined &&
        typeof rawVal === "object" &&
        !Array.isArray(rawVal)
      ) {
        // 3-level: value is a nested object with field-value pairs
        fields = processOddsEntry(rawVal as OddsEntry);
      } else {
        // A _lines array with a flagged main line wins outright; one
        // missing a flagged main gets the flat point/price merged in as
        // the main line instead (see resolveLineFields).
        const linesVal = sbOdds[`${oddsType}_lines`];
        const pointVal = sbOdds[`${oddsType}_point`];
        const priceVal = sbOdds[`${oddsType}_price`];
        const resolvedLines = resolveLineFields(
          linesVal,
          pointVal,
          priceVal,
          "",
          oddsType,
        );
        if (resolvedLines !== null) {
          fields = resolvedLines;
        } else if (pointVal !== undefined || priceVal !== undefined) {
          // 2-level: look for _point/_price pair, or fall back to direct value
          const parts: string[] = [];
          const p = formatPoint(pointVal);
          if (p) parts.push(p);
          const pr = formatOdds(priceVal);
          if (pr !== "-") parts.push(pointVal !== undefined ? `(${pr})` : pr);
          const display = parts.join(" ");
          fields = display ? [{ label: "", display, key: oddsType }] : [];
        } else {
          const display = formatOdds(rawVal);
          fields =
            display === "-" ? [] : [{ label: "", display, key: oddsType }];
        }
      }

      return fields.length > 0 ? { book, fields } : null;
    })
    .filter((b): b is { book: string; fields: SubgroupField[] } => b !== null);

  if (renderedBooks.length === 0) return null;

  return (
    <CollapsibleSection
      title={
        displayLabel ?? getOddsTypeDisplayLabel(oddsType, labelOverrides)
      }
      indent
      onChartClick={onChartClick ? () => onChartClick(chartKey) : undefined}
      highlighted={matchedTriggers.length > 0}
    >
      <TriggerAlert triggers={matchedTriggers} />
      <div style={{ background: "#000" }}>
        {renderedBooks.map(({ book, fields }, bi) => (
          <div
            key={book}
            style={{
              borderBottom:
                bi < renderedBooks.length - 1 ? "1px solid #3a3a3a" : "none",
            }}
          >
            {fields.map((field, fi) => (
              <div
                key={field.key}
                className="font_family_base"
                style={ROW_STYLE}
              >
                <span style={{ fontWeight: 600, color: "#d1d5db" }}>
                  {fi === 0 ? book : ""}
                </span>
                <span
                  style={{
                    color: field.isAlt ? "#a0a0a0" : "#9ca3af",
                    textAlign: "center",
                  }}
                >
                  {field.label}
                </span>
                <span
                  style={{
                    color: field.isAlt ? "#e0e0e0" : "#fff",
                    textAlign: "right",
                  }}
                >
                  {field.display}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
}

// ── Odds map section (Game Props / Player Props wrapper) ──────────────────────

function OddsMapSection({
  oddsMap,
  emptyMessage,
  onChartClick,
  excludeTypes,
  triggers = [],
  mergeTypes,
  labelOverrides,
  memberResolvers,
}: {
  oddsMap: GameOddsMap;
  emptyMessage: string;
  onChartClick?: (key: string) => void;
  excludeTypes?: Set<string>;
  triggers?: PropTrigger[];
  mergeTypes?: MergeTypes;
  labelOverrides?: LabelOverrides;
  memberResolvers?: MemberResolvers;
}) {
  const types = collectTypes(oddsMap, mergeTypes)
    .filter((t) => !excludeTypes?.has(t))
    .sort((a, b) =>
      getOddsTypeDisplayLabel(a, labelOverrides).localeCompare(
        getOddsTypeDisplayLabel(b, labelOverrides),
      ),
    );
  if (types.length === 0) {
    return (
      <p
        className="font_family_base"
        style={{ color: "#9ca3af", padding: "16px 16px", fontSize: 14 }}
      >
        {emptyMessage}
      </p>
    );
  }
  // Partition types into prefix groups and ungrouped, preserving order
  const seenPrefixGroups = new Set<string>();
  const orderedItems: React.ReactNode[] = [];

  types.forEach((t) => {
    let matchedGroup: string | null = null;
    for (const [gk, { prefix }] of Object.entries(PREFIX_GROUPS_DISPLAY)) {
      if (t.startsWith(prefix)) {
        matchedGroup = gk;
        break;
      }
    }

    if (matchedGroup) {
      if (!seenPrefixGroups.has(matchedGroup)) {
        seenPrefixGroups.add(matchedGroup);
        const { label, prefix } = PREFIX_GROUPS_DISPLAY[matchedGroup];
        const groupTypes = types
          .filter((type) => type.startsWith(prefix))
          .sort((a, b) =>
            toTitleLabel(a.replace(prefix, "")).localeCompare(
              toTitleLabel(b.replace(prefix, "")),
            ),
          );
        // A type can be "seen" here just from a raw key's presence even when
        // every book's value for it is null/empty — call OddsTypeSection
        // directly (it's a plain function, no hooks of its own) so we can
        // drop it, and skip the whole prefix group's wrapper, when nothing
        // in it actually renders.
        const renderedSections = groupTypes
          .map((gt) => {
            const el = OddsTypeSection({
              oddsType: gt,
              oddsMap,
              onChartClick,
              displayLabel: toTitleLabel(gt.replace(prefix, "")),
              triggers,
              mergeTypes,
              labelOverrides,
              memberResolvers,
            });
            return el === null ? null : cloneElement(el, { key: gt });
          })
          .filter((el) => el !== null);

        if (renderedSections.length === 0) return;

        const groupHighlighted = groupTypes.some(
          (gt) => triggersForOddsType(triggers, gt, mergeTypes).length > 0,
        );
        orderedItems.push(
          <CollapsibleSection
            key={matchedGroup}
            title={label}
            indent
            highlighted={groupHighlighted}
          >
            <div
              style={{
                padding: "4px 8px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {renderedSections}
            </div>
          </CollapsibleSection>,
        );
      }
    } else {
      orderedItems.push(
        <OddsTypeSection
          key={t}
          oddsType={t}
          oddsMap={oddsMap}
          onChartClick={onChartClick}
          triggers={triggers}
          mergeTypes={mergeTypes}
          labelOverrides={labelOverrides}
          memberResolvers={memberResolvers}
        />,
      );
    }
  });

  const rawGameFields = new Set<string>();
  if (DEBUG_GAME_SECTIONS) {
    Object.values(oddsMap).forEach((sbOdds) => {
      Object.keys(sbOdds).forEach((f) => rawGameFields.add(f));
    });
  }

  return (
    <div
      style={{
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {DEBUG_GAME_SECTIONS && (
        <div
          style={{
            border: "1px dashed #f59e0b",
            borderRadius: 8,
            padding: "10px 14px",
            background: "#1c1700",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#f59e0b",
              fontSize: 12,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            DEBUG — Raw game field names ({rawGameFields.size})
          </p>
          {[...rawGameFields].sort().map((name) => (
            <p
              key={name}
              className="font_family_base"
              style={{
                color: "#fcd34d",
                fontSize: 11,
                margin: "2px 0",
                fontFamily: "monospace",
              }}
            >
              {name}
            </p>
          ))}
        </div>
      )}
      {orderedItems}
    </div>
  );
}

const PLAYER_SECTION_LABELS: Record<string, string> = {
  //  player_batting_basesOnBalls: "Batting Walks",
  player_batting_doubles: "Doubles",
  player_batting_hits: "Hits",
  player_batting_home_runs: "Home Runs",
  player_batting_rbi: "RBIs",
  player_batting_singles: "Singles",
  player_batting_stolen_bases: "Stolen Bases",
  "player_batting_hits+runs+rbi": "Hits+Runs+RBIs",
  player_batting_triples: "Triples",
  player_batting_total_bases: "Total Bases",
  player_batting_strikeouts: "Strikeouts",
  player_strikeouts: "Strikeouts",
  player_pitching_bases_on_balls: "Bases On Balls",
  player_pitching_earned_runs: "Earned Runs",
  player_pitching_hits: "Hits",
  player_pitching_outs: "Outs",
  player_pitching_pitches_thrown: "Pitches Thrown",
  player_pitching_strikeouts: "Strikeouts",
  player_first_to_score: "First to Score",
  player_clearances: "Clearances",
  player_goalie_saves: "Goalie Saves Over/Under",
  player_passes_attempted: "Passes Attempted",
  player_shots_assisted: "Shots Assisted",
  player_shots_on_goal: "Shots on Goal",
  player_shots: "Shots",
  player_tackles: "Tackles",
  player_goals: "Goals",
  player_assists: "Record an Assist",
  player_shots_in_each_half: "At Least One Shot In Each Half",
  player_shots_on_target_in_each_half:
    "At Least One Shot On Target In Each Half",
  player_to_score_or_assist: "At Least One Score Or Assist",
  player_points: "Runs",
  player_rushing_receiving_yards: "Rushing and Receiving Yards",
  most_points_player_price: "Most Points",
  first_touchdown_scorer_price: "First Touchdown Scorer",
  last_touchdown_scorer_price: "Last Touchdown Scorer",
  anytime_touchdown_scorer_price: "Anytime Touchdown Scorer",
  player_p1_points: "1st Period Goals",
};

const PLAYER_SECTION_ALIASES: Record<string, string> = {
  player_batting_strikeouts: "player_strikeouts",
  // Same market (pitcher strikeouts thrown), named differently by different
  // sportsbooks — merge them so they render as one "Strikeouts" group.
  player_pitching_strikeouts: "player_strikeouts",
};

const PLAYER_PREFIX_GROUPS: { label: string; prefix: string }[] = [
  { label: "Batting", prefix: "player_batting_" },
  { label: "Batting", prefix: "player_points" },
  // player_strikeouts (and its player_batting_strikeouts alias) is a
  // pitcher's strikeouts-thrown prop, not a batter's own strikeouts.
  { label: "Pitching", prefix: "player_strikeouts" },
  { label: "Pitching", prefix: "player_pitching_" },
];

function getPlayerSectionGroup(
  sectionName: string,
  isNfl: boolean,
  isNhl: boolean,
): string | null {
  // "player_points" is a baseball "Runs" stat (grouped under Batting), but the
  // same field name is reused by the NFL/NHL feeds for a different, standalone
  // stat — don't fold it into the baseball Batting group there.
  if ((isNfl || isNhl) && sectionName.startsWith("player_points")) return null;
  for (const { label, prefix } of PLAYER_PREFIX_GROUPS) {
    if (sectionName.startsWith(prefix)) return label;
  }
  return null;
}

function getPlayerStatLabel(
  baseName: string,
  isNfl: boolean,
  isNhl: boolean,
  kind: "line" | "simple",
  isMls = false,
): string {
  if (isMls) {
    if (kind === "line" && baseName.startsWith("player_assists"))
      return "Assist Over/Under";
    if (baseName === "player_goals") return "Goals Over/Under";
    if (baseName === "player_shots") return "Shots Over/Under";
  }
  if (isNfl && baseName.startsWith("player_points")) return "Player Points";
  // Only football offers this market, so no sport check; "includes" also covers
  // a "player_" prefixed key, which would otherwise fall through to a title-cased label.
  if (baseName.includes("anytime_touchdown_scorer")) return "Touchdown Scorer";
  // Football-only stat (NFL and NCAAF), so likewise no sport check.
  if (baseName.includes("passing_rushing_yards"))
    return "Passing and Rushing Yards";
  if (isNhl && baseName.startsWith("player_points"))
    return kind === "line" ? "Goals Over/Under" : "Goals (Yes)";
  if (baseName === "player_scores") return "Goals (Yes)";
  // These stats are offered both as an over/under line and as a flat yes
  // market — same base name, so distinguish the two by section kind.
  if (baseName.startsWith("player_shots_on_goal"))
    return kind === "line" ? "Shots on Goal Over/Under" : "Shots on Goal (Yes)";
  if (
    baseName.startsWith("player_goals_and_assist") ||
    baseName.startsWith("player_goals_assist")
  )
    return kind === "line"
      ? "Goals and Assists Over/Under"
      : "Goals and Assists (Yes)";
  if (baseName.startsWith("player_assists"))
    return kind === "line"
      ? "Record an Assist Over/Under"
      : "Record an Assist (Yes)";
  return (
    PLAYER_SECTION_LABELS[baseName] ??
    toTitleLabel(baseName.replace(/^player_/, ""))
  );
}

// A trigger's prop_stem names the stat field, with or without the trailing
// over/under + point/price suffix depending on the market — strip both so it
// lines up with the section base names built above (sectionName / baseName).
// Trigger-only aliases: triggers on these stems belong to another section's
// odds. Kept apart from PLAYER_SECTION_ALIASES so the odds themselves aren't merged.
const PLAYER_TRIGGER_STEM_ALIASES: Record<string, string> = {
  // "Batting Runs" odds are hidden (see HIDDEN_PLAYER_SECTION_RE) since they
  // duplicate Batting → Runs, so its triggers attach there.
  player_batting_runs: "player_points",
};

function normalizePlayerStem(stem: string): string {
  const base = stem
    .replace(/_point$|_price$|_lines$/, "")
    .replace(/_(over|under)$/, "");
  const aliased = PLAYER_SECTION_ALIASES[base] ?? base;
  return PLAYER_TRIGGER_STEM_ALIASES[aliased] ?? aliased;
}

function triggersForPlayerSection(
  triggers: PropTrigger[],
  sectionBase: string,
  playerId: string,
): PropTrigger[] {
  return triggers.filter(
    (t) =>
      t.player_id === playerId &&
      !!t.prop_stem &&
      normalizePlayerStem(t.prop_stem) === sectionBase,
  );
}

// ── Player Odds section ───────────────────────────────────────────────────────
// Keys follow "<section_name>:<player_id>" format.
// Section names ending in _(over|under)_(point|price) are combined into "Line (Stat)" groups.

const DEBUG_PLAYER_SECTIONS = false; // toggle to false to hide
const DEBUG_GAME_SECTIONS = false; // toggle to false to hide

const SIDE_SUFFIX_RE = /_(over|under)_(point|price|lines)$/;
const SIMPLE_SIDE_RE = /_(over|under)$/;

// Player prop sections where the line value is fixed/meaningless — show price only
const PRICE_ONLY_PLAYER_SECTIONS = new Set([
  "player_assists",
  "player_shots_in_each_half",
  "player_shots_on_target_in_each_half",
  "player_to_score_or_assist",
]);

// Player prop sections to skip entirely for a given sport (e.g. NFL kicker
// field goals — not useful enough to surface as a group).
const HIDDEN_NFL_PLAYER_SECTION_PREFIXES = [
  "player_field_goals_made",
  "player_points",
];

// Baseball "Batting Runs" duplicates the "Runs" (player_points) market, so it
// is not shown as its own section (matches the bare name and its _over/_under
// and _point/_price/_lines variants, which stay attached ahead of ":playerId").
const HIDDEN_PLAYER_SECTION_RE =
  /^player_batting_runs(_(over|under))?(_(point|price|lines))?$/;

// Section bases that should be merged from separate _over/_under simple sections
const PLAYER_OVER_UNDER_PAIRS = new Set([
  "player_batting_hits",
  "player_batting_strikeouts",
  "player_strikeouts",
]);

function getLineSectionInfo(
  sectionName: string,
): { baseName: string; side: string } | null {
  const m = sectionName.match(SIDE_SUFFIX_RE);
  if (m) return { baseName: sectionName.slice(0, -m[0].length), side: m[1] };
  const sm = sectionName.match(SIMPLE_SIDE_RE);
  if (sm) {
    const base = sectionName.slice(0, -sm[0].length);
    if (PLAYER_OVER_UNDER_PAIRS.has(base))
      return { baseName: base, side: sm[1] };
  }
  return null;
}

function renderPlayerRows(
  playerKeys: string[],
  oddsMap: GameOddsMap,
  getFields: (sbOdds: OddsEntry, playerKey: string) => SubgroupField[],
  playerMetadata: Record<string, string> = {},
  onChartClick?: (key: string) => void,
  sectionBase?: string,
  triggers: PropTrigger[] = [],
) {
  return playerKeys.map((playerKey, pi) => {
    const colonIdx = playerKey.indexOf(":");
    const playerId =
      colonIdx !== -1 ? playerKey.substring(colonIdx + 1).trim() : playerKey;
    const displayName = playerMetadata[playerId] ?? playerId;

    const booksWithData: { book: string; fields: SubgroupField[] }[] = [];
    Object.entries(oddsMap).forEach(([book, sbOdds]) => {
      const fields = getFields(sbOdds, playerKey);
      if (fields.length > 0) booksWithData.push({ book, fields });
    });

    if (booksWithData.length === 0) return null;

    const matchedTriggers = sectionBase
      ? triggersForPlayerSection(triggers, sectionBase, playerId)
      : [];

    return (
      <div
        key={playerKey}
        style={
          matchedTriggers.length > 0
            ? {
                border: "2px solid #ff0000",
                borderRadius: 10,
                overflow: "hidden",
              }
            : undefined
        }
      >
        <div
          className="font_family_base"
          style={{
            padding: "5px 14px",
            fontSize: 12,
            color: "#9ca3af",
            background: "#111",
            borderTop:
              pi > 0 && matchedTriggers.length === 0
                ? "1px solid #333"
                : "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{displayName}</span>
          {onChartClick && (
            <span
              onClick={() =>
                onChartClick(playerKey.replace(/_(over|under)(:|$)/, "$2"))
              }
              style={{ fontSize: 16, cursor: "pointer", lineHeight: 1 }}
            >
              📊
            </span>
          )}
        </div>
        <TriggerAlert triggers={matchedTriggers} />
        {booksWithData.map(({ book, fields }, bi) => (
          <div
            key={book}
            style={{
              borderBottom:
                bi < booksWithData.length - 1 ? "1px solid #3a3a3a" : "none",
            }}
          >
            {fields.map((field, fi) => (
              <div
                key={field.key}
                className="font_family_base"
                style={ROW_STYLE}
              >
                <span style={{ fontWeight: 600, color: "#d1d5db" }}>
                  {fi === 0 ? book : ""}
                </span>
                <span style={{ color: "#9ca3af", textAlign: "center" }}>
                  {field.label}
                </span>
                <span style={{ color: "#fff", textAlign: "right" }}>
                  {field.display}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  });
}

function PlayerOddsSection({
  oddsMap,
  emptyMessage,
  onChartClick,
  playerMetadata = {} as Record<string, string>,
  triggers = [],
  isNfl = false,
  isNhl = false,
  isMls = false,
  isNcaaf = false,
}: {
  oddsMap: GameOddsMap;
  emptyMessage: string;
  onChartClick?: (key: string) => void;
  playerMetadata?: Record<string, string>;
  triggers?: PropTrigger[];
  isNfl?: boolean;
  isNhl?: boolean;
  isMls?: boolean;
  isNcaaf?: boolean;
}) {
  // Simple sections: sectionName → [playerKey, ...]
  const simpleSectionMap = new Map<string, string[]>();
  // Line groups: baseName → { label, playerIds, lookupBases }
  const lineGroupMap = new Map<
    string,
    { label: string; playerIds: string[]; lookupBases: string[] }
  >();
  const rawSectionNames = new Set<string>();

  Object.values(oddsMap).forEach((sbOdds) => {
    Object.keys(sbOdds).forEach((field) => {
      // Strips a bare (no ":player") suffix. Player-scoped fields keep their
      // "_point"/"_price"/"_lines" suffix ahead of the ":player" part instead —
      // getLineSectionInfo (below) is what actually recognizes those.
      const base = field.replace(/_point$|_price$|_lines$/, "");
      const colonIdx = base.indexOf(":");
      const rawSection =
        colonIdx !== -1 ? base.substring(0, colonIdx).trim() : base;
      const sectionName = PLAYER_SECTION_ALIASES[rawSection] ?? rawSection;
      if (HIDDEN_PLAYER_SECTION_RE.test(sectionName)) return;
      // NCAAF has no baseball stats — the "Batting" group there would only be
      // the baseball-named "Runs" (player_points) feed, so hide it entirely.
      if (
        isNcaaf &&
        getPlayerSectionGroup(sectionName, false, false) === "Batting"
      )
        return;
      if (
        isNfl &&
        HIDDEN_NFL_PLAYER_SECTION_PREFIXES.some((p) =>
          sectionName.startsWith(p),
        )
      )
        return;
      const playerId =
        colonIdx !== -1 ? base.substring(colonIdx + 1).trim() : "";
      rawSectionNames.add(sectionName);

      const lineInfo = getLineSectionInfo(sectionName);
      if (lineInfo) {
        const rawBaseName = lineInfo.baseName;
        const baseName = PLAYER_SECTION_ALIASES[rawBaseName] ?? rawBaseName;
        if (!lineGroupMap.has(baseName)) {
          const statLabel = getPlayerStatLabel(
            baseName,
            isNfl,
            isNhl,
            "line",
            isMls,
          );
          lineGroupMap.set(baseName, {
            label: statLabel,
            playerIds: [],
            lookupBases: [baseName],
          });
        }
        const grp = lineGroupMap.get(baseName)!;
        if (!grp.lookupBases.includes(rawBaseName))
          grp.lookupBases.push(rawBaseName);
        const pidKey = playerId || "__flat__";
        if (!grp.playerIds.includes(pidKey)) grp.playerIds.push(pidKey);
      } else {
        const existing = simpleSectionMap.get(sectionName) ?? [];
        if (!existing.includes(base)) {
          existing.push(base);
          simpleSectionMap.set(sectionName, existing);
        }
      }
    });
  });

  type AnySection =
    | {
        kind: "simple";
        sectionName: string;
        label: string;
        playerKeys: string[];
      }
    | {
        kind: "line";
        baseName: string;
        label: string;
        playerIds: string[];
        lookupBases: string[];
      };

  const sortKey = (name: string) => {
    const parts = name.trim().split(/\s+/);
    const last = parts.length > 1 ? parts[parts.length - 1] : parts[0];
    const first = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
    return `${last}\0${first}`.toLowerCase();
  };
  const sortPlayerIds = (ids: string[]) =>
    [...ids].sort((a, b) =>
      sortKey(playerMetadata[a] ?? a).localeCompare(
        sortKey(playerMetadata[b] ?? b),
      ),
    );
  const sortPlayerKeys = (keys: string[]) =>
    [...keys].sort((a, b) => {
      const idA = a.includes(":") ? a.substring(a.indexOf(":") + 1) : a;
      const idB = b.includes(":") ? b.substring(b.indexOf(":") + 1) : b;
      return sortKey(playerMetadata[idA] ?? idA).localeCompare(
        sortKey(playerMetadata[idB] ?? idB),
      );
    });

  const allSections: AnySection[] = [
    ...[...simpleSectionMap.entries()].map(([sectionName, playerKeys]) => ({
      kind: "simple" as const,
      sectionName,
      label: getPlayerStatLabel(sectionName, isNfl, isNhl, "simple", isMls),
      playerKeys: sortPlayerKeys(playerKeys),
    })),
    ...[...lineGroupMap.entries()].map(
      ([baseName, { label, playerIds, lookupBases }]) => ({
        kind: "line" as const,
        baseName,
        label,
        playerIds: sortPlayerIds(playerIds),
        lookupBases,
      }),
    ),
  ].sort((a, b) => a.label.localeCompare(b.label));

  if (allSections.length === 0) {
    return (
      <p
        className="font_family_base"
        style={{ color: "#9ca3af", padding: "16px 16px", fontSize: 14 }}
      >
        {emptyMessage}
      </p>
    );
  }

  return (
    <div
      style={{
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {DEBUG_PLAYER_SECTIONS && (
        <div
          style={{
            border: "1px dashed #f59e0b",
            borderRadius: 8,
            padding: "10px 14px",
            background: "#1c1700",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#f59e0b",
              fontSize: 12,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            DEBUG — Raw section names ({rawSectionNames.size})
          </p>
          {[...rawSectionNames].sort().map((name) => (
            <p
              key={name}
              className="font_family_base"
              style={{
                color: "#fcd34d",
                fontSize: 11,
                margin: "2px 0",
                fontFamily: "monospace",
              }}
            >
              {name}
            </p>
          ))}
        </div>
      )}
      {(() => {
        const sectionHasMatch = (
          section: (typeof allSections)[number],
        ): boolean => {
          if (section.kind === "simple") {
            return section.playerKeys.some((key) => {
              const colonIdx = key.indexOf(":");
              const playerId =
                colonIdx !== -1 ? key.substring(colonIdx + 1).trim() : key;
              return (
                triggersForPlayerSection(
                  triggers,
                  section.sectionName,
                  playerId,
                ).length > 0
              );
            });
          }
          return section.playerIds.some(
            (playerId) =>
              playerId !== "__flat__" &&
              triggersForPlayerSection(triggers, section.baseName, playerId)
                .length > 0,
          );
        };

        const renderSection = (section: (typeof allSections)[number]) => {
          if (section.kind === "simple") {
            const { sectionName, label, playerKeys } = section;
            const playerRows = renderPlayerRows(
              playerKeys,
              oddsMap,
              (sbOdds, playerKey) => {
                const linesVal = sbOdds[`${playerKey}_lines`];
                if (Array.isArray(linesVal) && linesVal.length > 0) {
                  return processLinesArray(linesVal, "", playerKey);
                }
                const rawVal = sbOdds[playerKey];
                if (
                  rawVal !== null &&
                  rawVal !== undefined &&
                  typeof rawVal === "object" &&
                  !Array.isArray(rawVal)
                ) {
                  return processOddsEntry(rawVal as OddsEntry);
                }
                const pointVal = sbOdds[`${playerKey}_point`];
                const priceVal = sbOdds[`${playerKey}_price`];
                if (pointVal !== undefined || priceVal !== undefined) {
                  const parts: string[] = [];
                  const p = formatPoint(pointVal);
                  if (p) parts.push(p);
                  const pr = formatOdds(priceVal);
                  if (pr !== "-")
                    parts.push(pointVal !== undefined ? `(${pr})` : pr);
                  const display = parts.join(" ");
                  return display
                    ? [{ label: "", display, key: playerKey }]
                    : [];
                }
                const display = formatOdds(rawVal);
                return display !== "-"
                  ? [{ label: "", display, key: playerKey }]
                  : [];
              },
              playerMetadata,
              onChartClick ?? undefined,
              sectionName,
              triggers,
            );
            if (playerRows.every((r) => r === null)) return null;
            return (
              <CollapsibleSection
                key={`simple:${sectionName}`}
                title={label}
                indent
                highlighted={sectionHasMatch(section)}
              >
                <div style={{ background: "#000" }}>{playerRows}</div>
              </CollapsibleSection>
            );
          }
          // Line section: Over/Under rows per player
          const {
            baseName,
            label: lineLabel,
            playerIds,
            lookupBases,
          } = section;
          const playerNodes = playerIds.map((playerId, pi) => {
            const booksWithData: {
              book: string;
              rows: { label: string; display: string; key: string }[];
            }[] = [];

            const isFlat = playerId === "__flat__";
            const colonSuffix = isFlat ? "" : `:${playerId}`;

            Object.entries(oddsMap).forEach(([book, sbOdds]) => {
              const rows: {
                label: string;
                display: string;
                key: string;
              }[] = [];
              const priceOnly = PRICE_ONLY_PLAYER_SECTIONS.has(baseName);
              for (const [side, rowLabel] of [
                ["over", "Over"],
                ["under", "Under"],
              ]) {
                if (priceOnly && side === "under") continue;
                // Try all source base names (handles aliased merges,
                // e.g. player_strikeouts vs. player_pitching_strikeouts)
                // — gather each piece independently since one prefix
                // may carry the alt-line list while another only has
                // the flat point/price.
                let pointVal: unknown,
                  priceVal: unknown,
                  simpleVal: unknown,
                  linesVal: unknown;
                for (const lb of lookupBases) {
                  if (linesVal === undefined) {
                    const lv = sbOdds[`${lb}_${side}_lines${colonSuffix}`];
                    if (Array.isArray(lv) && lv.length > 0) linesVal = lv;
                  }
                  if (pointVal === undefined)
                    pointVal = sbOdds[`${lb}_${side}_point${colonSuffix}`];
                  if (priceVal === undefined)
                    priceVal = sbOdds[`${lb}_${side}_price${colonSuffix}`];
                  if (simpleVal === undefined)
                    simpleVal = sbOdds[`${lb}_${side}${colonSuffix}`];
                }
                // A _lines array with a flagged main line wins outright
                // (the flat point/price is redundant with it); a list
                // missing a flagged main gets the flat value merged in
                // as the main line instead.
                const resolvedLines = resolveLineFields(
                  linesVal,
                  pointVal,
                  priceVal,
                  priceOnly ? "" : rowLabel,
                  `${baseName}_${side}${colonSuffix}`,
                );
                if (resolvedLines !== null) {
                  resolvedLines.forEach((f) =>
                    rows.push({
                      label: f.label,
                      display: f.display,
                      key: f.key,
                    }),
                  );
                  continue;
                }
                if (
                  pointVal === undefined &&
                  priceVal === undefined &&
                  simpleVal === undefined
                )
                  continue;
                let display: string;
                if (simpleVal !== undefined) {
                  display = formatOdds(simpleVal);
                } else {
                  const parts: string[] = [];
                  if (!priceOnly) {
                    const p = formatPoint(pointVal);
                    if (p) parts.push(p);
                  }
                  const pr = formatOdds(priceVal);
                  if (pr !== "-")
                    parts.push(
                      !priceOnly && pointVal !== undefined ? `(${pr})` : pr,
                    );
                  display = parts.join(" ");
                }
                if (display && display !== "-")
                  rows.push({
                    label: priceOnly ? "" : rowLabel,
                    display,
                    key: `${baseName}_${side}${colonSuffix}`,
                  });
              }
              if (rows.length > 0) booksWithData.push({ book, rows });
            });

            if (booksWithData.length === 0) return null;

            const matchedTriggers = !isFlat
              ? triggersForPlayerSection(triggers, baseName, playerId)
              : [];

            return (
              <div
                key={playerId}
                style={
                  matchedTriggers.length > 0
                    ? {
                        border: "2px solid #ff0000",
                        borderRadius: 10,
                        overflow: "hidden",
                      }
                    : undefined
                }
              >
                {!isFlat && (
                  <div
                    className="font_family_base"
                    style={{
                      padding: "5px 14px",
                      fontSize: 12,
                      color: "#9ca3af",
                      background: "#111",
                      borderTop:
                        pi > 0 && matchedTriggers.length === 0
                          ? "1px solid #333"
                          : "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{playerMetadata[playerId] ?? playerId}</span>
                    {onChartClick && (
                      <span
                        onClick={() => onChartClick(`${baseName}:${playerId}`)}
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                          lineHeight: 1,
                        }}
                      >
                        📊
                      </span>
                    )}
                  </div>
                )}
                {!isFlat && <TriggerAlert triggers={matchedTriggers} />}
                {booksWithData.map(({ book, rows }, bi) => (
                  <div
                    key={book}
                    style={{
                      borderBottom:
                        bi < booksWithData.length - 1
                          ? "1px solid #3a3a3a"
                          : "none",
                    }}
                  >
                    {rows.map((row, ri) => (
                      <div
                        key={row.key}
                        className="font_family_base"
                        style={ROW_STYLE}
                      >
                        <span style={{ fontWeight: 600, color: "#d1d5db" }}>
                          {ri === 0 ? book : ""}
                        </span>
                        <span
                          style={{
                            color: "#9ca3af",
                            textAlign: "center",
                          }}
                        >
                          {row.label}
                        </span>
                        <span style={{ color: "#fff", textAlign: "right" }}>
                          {row.display}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          });
          if (playerNodes.every((n) => n === null)) return null;
          return (
            <CollapsibleSection
              key={`line:${baseName}`}
              title={lineLabel}
              indent
              highlighted={sectionHasMatch(section)}
            >
              <div style={{ background: "#000" }}>{playerNodes}</div>
            </CollapsibleSection>
          );
        };

        const sectionGroup = (s: (typeof allSections)[number]) =>
          getPlayerSectionGroup(
            s.kind === "simple" ? s.sectionName : s.baseName,
            isNfl,
            isNhl,
          );

        const allGroups = [
          ...new Set(allSections.map(sectionGroup).filter(Boolean) as string[]),
        ].sort();
        const nodes: React.ReactNode[] = [];

        // Render named prefix groups alphabetically
        allGroups.forEach((group) => {
          const groupSections = allSections.filter(
            (s) => sectionGroup(s) === group,
          );
          const renderedSections = groupSections
            .map((s) => renderSection(s))
            .filter((el) => el !== null);
          if (renderedSections.length === 0) return;
          const groupHighlighted = groupSections.some((s) =>
            sectionHasMatch(s),
          );
          nodes.push(
            <CollapsibleSection
              key={group}
              title={group}
              indent
              highlighted={groupHighlighted}
            >
              <div
                style={{
                  padding: "4px 8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {renderedSections}
              </div>
            </CollapsibleSection>,
          );
        });

        // Render ungrouped sections after
        allSections
          .filter((s) => !sectionGroup(s))
          .forEach((s) => {
            const el = renderSection(s);
            if (el !== null) nodes.push(el);
          });

        return nodes;
      })()}
    </div>
  );
}

// ── Main Odds grouped section ────────────────────────────────────────────────

type SubgroupField = {
  label: string;
  display: string;
  key: string;
  // The real, directly-fetchable chart key for this field (e.g. "away_spread_point").
  // Distinct from `key` because `key` is sometimes a bare base or a synthetic
  // per-alt-line id used only for React list identity, not an actual API field.
  chartKey?: string;
  isAlt?: boolean;
};

type SubgroupDef = {
  key: string;
  label: string;
  getFields: (entry: OddsEntry) => SubgroupField[];
};

function formatPoint(val: unknown): string {
  if (val === null || val === undefined || val === "") return "";
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return n % 1 === 0 ? `${n}.0` : String(n);
}

interface LineEntry {
  point: number;
  price: number;
  is_main_line: boolean;
}

function processLinesArray(
  linesVal: unknown,
  mainLabel: string,
  keyBase: string,
  chartKey?: string,
): SubgroupField[] {
  if (!Array.isArray(linesVal) || linesVal.length === 0) return [];
  const lines = linesVal as LineEntry[];
  const sorted = [...lines].sort((a, b) => a.point - b.point);
  const showSuffix =
    lines.some((l) => l.is_main_line) && lines.some((l) => !l.is_main_line);
  return sorted.flatMap((line, i) => {
    const p = formatPoint(line.point);
    const pr = formatOdds(line.price);
    const display = [p, pr !== "-" ? `(${pr})` : ""].filter(Boolean).join(" ");
    if (!display) return [];
    const label = showSuffix
      ? mainLabel
        ? `${mainLabel} ${line.is_main_line ? "(main)" : "(alt)"}`
        : line.is_main_line
          ? "(main)"
          : "(alt)"
      : mainLabel;
    return [
      {
        label,
        display,
        key: `${keyBase}_line_${i}`,
        chartKey,
        isAlt: !line.is_main_line,
      },
    ];
  });
}

// Reconciles a "_lines" alt-line array with its flat "_point"/"_price"
// siblings when both exist for the same market (different sportsbooks
// sometimes report the same stat under different field-name prefixes, one
// with the richer list and one with just a flat value). A list entry flagged
// is_main_line wins outright — the flat value is redundant with it, so it's
// ignored. A list present but missing a flagged main line gets the flat
// point/price merged in as the main line. Returns null (not []) when there's
// no usable list at all, so the caller knows to fall back to flat rendering.
function resolveLineFields(
  linesVal: unknown,
  pointVal: unknown,
  priceVal: unknown,
  mainLabel: string,
  keyBase: string,
  chartKey?: string,
): SubgroupField[] | null {
  if (!Array.isArray(linesVal) || linesVal.length === 0) return null;
  const lines = linesVal as LineEntry[];
  if (lines.some((l) => l.is_main_line)) {
    return processLinesArray(lines, mainLabel, keyBase, chartKey);
  }
  const pointNum = Number(pointVal);
  const priceNum = Number(priceVal);
  if (
    pointVal === undefined ||
    priceVal === undefined ||
    isNaN(pointNum) ||
    isNaN(priceNum)
  ) {
    return processLinesArray(lines, mainLabel, keyBase, chartKey);
  }
  return processLinesArray(
    [...lines, { point: pointNum, price: priceNum, is_main_line: true }],
    mainLabel,
    keyBase,
    chartKey,
  );
}

const SUBGROUPS: SubgroupDef[] = [
  {
    key: "line",
    label: "Line",
    getFields: (entry) => {
      // Pair _point and _price by shared prefix (e.g. over_total, under_total)
      const prefixes: string[] = [];
      Object.keys(entry).forEach((k) => {
        if (k.endsWith("_lines")) return; // handled via lines array below
        if (/over|under|total|_line/.test(k.toLowerCase())) {
          const base = k.replace(/_point$|_price$/, "");
          if (!prefixes.includes(base)) prefixes.push(base);
        }
      });
      return prefixes.flatMap((base) => {
        const baseLabel = toTitleLabel(
          base.replace(/_total$/, "").replace(/_line$/, ""),
        );
        const point = entry[`${base}_point`];
        const price = entry[`${base}_price`];
        const chartKey =
          point !== undefined
            ? `${base}_point`
            : price !== undefined
              ? `${base}_price`
              : base;
        // A _lines array with a flagged main line wins outright; one missing
        // a flagged main gets the flat point/price merged in as the main
        // line instead (see resolveLineFields).
        const linesVal = entry[`${base}_lines`];
        const resolvedLines = resolveLineFields(
          linesVal,
          point,
          price,
          baseLabel,
          base,
          chartKey,
        );
        if (resolvedLines !== null) return resolvedLines;
        const parts: string[] = [];
        const p = formatPoint(point);
        if (p) parts.push(p);
        const pr = formatOdds(price);
        if (pr !== "-") parts.push(`(${pr})`);
        if (parts.length === 0) return [];
        return [
          { label: baseLabel, display: parts.join(" "), key: base, chartKey },
        ];
      });
    },
  },
  {
    key: "moneyline",
    label: "Moneyline",
    getFields: (entry) =>
      Object.keys(entry)
        .filter((f) => /outcome|moneyline|_ml/.test(f.toLowerCase()))
        .flatMap((f) => {
          const val = entry[f];
          if (val === null || val === undefined || val === "") return [];
          const label = f.toLowerCase().includes("outcome")
            ? toTitleLabel(f.split("_outcome")[0])
            : toTitleLabel(f);
          return [{ label, display: formatOdds(val), key: f, chartKey: f }];
        }),
  },
  {
    key: "spread",
    label: "Spread",
    getFields: (entry) => {
      const prefixes: string[] = [];
      Object.keys(entry).forEach((k) => {
        if (k.endsWith("_lines")) return; // handled via lines array below
        if (/spread|handicap/.test(k.toLowerCase())) {
          const base = k.replace(/_point$|_price$/, "");
          if (!prefixes.includes(base)) prefixes.push(base);
        }
      });
      return prefixes.flatMap((base) => {
        const baseLabel = toTitleLabel(
          base.replace(/_spread$/, "").replace(/_handicap$/, ""),
        );
        const point = entry[`${base}_point`];
        const price = entry[`${base}_price`];
        const chartKey =
          point !== undefined ? `${base}_point` : `${base}_price`;
        // A _lines array with a flagged main line wins outright; one missing
        // a flagged main gets the flat point/price merged in as the main
        // line instead (see resolveLineFields).
        const linesVal = entry[`${base}_lines`];
        const resolvedLines = resolveLineFields(
          linesVal,
          point,
          price,
          baseLabel,
          base,
          chartKey,
        );
        if (resolvedLines !== null) return resolvedLines;
        // If no _point/_price/_lines pattern, fall back to the raw field
        if (point === undefined && price === undefined) {
          const val = entry[base];
          if (val === null || val === undefined || val === "") return [];
          return [
            {
              label: toTitleLabel(base),
              display: formatOdds(val),
              key: base,
              chartKey: base,
            },
          ];
        }
        const parts: string[] = [];
        const p = formatPoint(point);
        if (p) parts.push(p);
        const pr = formatOdds(price);
        if (pr !== "-") parts.push(`(${pr})`);
        if (parts.length === 0) return [];
        return [
          { label: baseLabel, display: parts.join(" "), key: base, chartKey },
        ];
      });
    },
  },
];

function MainOddsSection({
  oddsMap,
  isSoccer,
  emptyMessage,
  onChartClick,
}: {
  oddsMap: SimpleOddsMap;
  isSoccer: boolean;
  emptyMessage: string;
  onChartClick?: (key: string) => void;
}) {
  if (Object.keys(oddsMap).length === 0) {
    return (
      <p
        className="font_family_base"
        style={{ color: "#9ca3af", padding: "16px 16px", fontSize: 14 }}
      >
        {emptyMessage}
      </p>
    );
  }

  const subgroups = (
    isSoccer ? SUBGROUPS.filter((sg) => sg.key === "moneyline") : [...SUBGROUPS]
  ).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div
      style={{
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {subgroups.map((sg) => {
        // Group into per-book buckets using getFields
        const bookGroups: { book: string; fields: SubgroupField[] }[] = [];
        Object.entries(oddsMap).forEach(([book, entry]) => {
          const fields = sg
            .getFields(entry)
            .map((f) => ({ ...f, key: `${book}-${f.key}` }));
          if (fields.length > 0) bookGroups.push({ book, fields });
        });

        if (bookGroups.length === 0) return null;

        // sg.key ("line"/"spread"/"moneyline") is just a UI grouping id, not a
        // real fetchable field — use the first field's actual chartKey instead.
        const chartKey =
          bookGroups.flatMap((bg) => bg.fields).find((f) => f.chartKey)
            ?.chartKey ?? sg.key;

        return (
          <CollapsibleSection
            key={sg.key}
            title={sg.label}
            indent
            onChartClick={
              onChartClick ? () => onChartClick(chartKey) : undefined
            }
          >
            <div style={{ background: "#000" }}>
              {bookGroups.map(({ book, fields }, bi) => (
                <div
                  key={book}
                  style={{
                    borderBottom:
                      bi < bookGroups.length - 1 ? "1px solid #3a3a3a" : "none",
                  }}
                >
                  {fields.map((field, fi) => (
                    <div
                      key={field.key}
                      className="font_family_base"
                      style={ROW_STYLE}
                    >
                      <span style={{ fontWeight: 600, color: "#d1d5db" }}>
                        {fi === 0 ? book : ""}
                      </span>
                      <span
                        style={{
                          color: field.isAlt ? "#a0a0a0" : "#9ca3af",
                          textAlign: "center",
                        }}
                      >
                        {field.label}
                      </span>
                      <span
                        style={{
                          color: field.isAlt ? "#e0e0e0" : "#fff",
                          textAlign: "right",
                        }}
                      >
                        {field.display}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function GameProps() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [propsData, setPropsData] = useState<PropsData | null>(null);
  const [rawResponse, setRawResponse] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastSuccessRef = useRef<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const redirectPath = AccessVerification() || ProAccessCheck();
    if (redirectPath) navigate(redirectPath);
  }, [navigate]);

  const fetchPropsData = useCallback(async (signal: AbortSignal) => {
    if (!gameId) return;
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        `/api/games_props_data/${gameId}/`,
        { signal },
      );
      if (signal.aborted) return;
      if ("error" in response) {
        const neverLoaded = lastSuccessRef.current === null;
        const stale =
          lastSuccessRef.current !== null &&
          Date.now() - lastSuccessRef.current >= 60_000;
        if (neverLoaded || stale) setError("Failed to load prop bet data.");
      } else {
        lastSuccessRef.current = Date.now();
        setRawResponse(response);
        setPropsData(response as PropsData);
        setError(null);
      }
    } catch {
      if (signal.aborted) return;
      const neverLoaded = lastSuccessRef.current === null;
      const stale =
        lastSuccessRef.current !== null &&
        Date.now() - lastSuccessRef.current >= 60_000;
      if (neverLoaded || stale) setError("Failed to load prop bet data.");
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, [gameId]);

  const startTimeValue = propsData?.event?.start_time;
  const gameNotStartedYet =
    !!startTimeValue && new Date(startTimeValue) > new Date();

  // Initial load + auto-refresh (5s, backs off to 15s on a slow connection)
  useAdaptivePolling(fetchPropsData, gameNotStartedYet ? 60_000 : 5_000);

  // Right after a game's start time passes, the live odds maps can be empty
  // for a bit before the live feed catches up — fall back to the pre-game
  // snapshot for each map independently rather than showing nothing.
  const liveMainOdds: SimpleOddsMap =
    (propsData?.event?.odds as SimpleOddsMap) ?? {};
  const startingMainOdds: SimpleOddsMap =
    (propsData?.event?.starting_odds as SimpleOddsMap) ?? {};
  const mainOddsIsPregame =
    Object.keys(liveMainOdds).length === 0 &&
    Object.keys(startingMainOdds).length > 0;
  const mainOdds: SimpleOddsMap = mainOddsIsPregame
    ? startingMainOdds
    : liveMainOdds;

  const liveGameOdds: GameOddsMap =
    (propsData?.event?.game_odds as GameOddsMap) ?? {};
  const startingGameOdds: GameOddsMap =
    (propsData?.event?.starting_game_odds as GameOddsMap) ?? {};
  const gameOddsIsPregame =
    Object.keys(liveGameOdds).length === 0 &&
    Object.keys(startingGameOdds).length > 0;
  const rawGameOdds: GameOddsMap = gameOddsIsPregame
    ? startingGameOdds
    : liveGameOdds;

  // Augment gameOdds with _lines arrays from mainOdds so compound groups
  // can render alt lines even when _lines only appear in event.odds.
  const gameOdds: GameOddsMap = {};
  Object.entries(rawGameOdds).forEach(([book, sbOdds]) => {
    const mainEntry = mainOdds[book] ?? {};
    const extraLines: Record<string, unknown> = {};
    Object.entries(mainEntry).forEach(([field, value]) => {
      if (field.endsWith("_lines") && !(field in sbOdds)) {
        extraLines[field] = value;
      }
    });
    gameOdds[book] = { ...extraLines, ...sbOdds } as SportsbookOdds;
  });

  const livePlayerOdds: GameOddsMap =
    (propsData?.event?.player_odds as GameOddsMap) ?? {};
  const startingPlayerOdds: GameOddsMap =
    (propsData?.event?.starting_player_odds as GameOddsMap) ?? {};
  const playerOddsIsPregame =
    Object.keys(livePlayerOdds).length === 0 &&
    Object.keys(startingPlayerOdds).length > 0;
  const playerOdds: GameOddsMap = playerOddsIsPregame
    ? startingPlayerOdds
    : livePlayerOdds;

  const showingPregameOdds =
    mainOddsIsPregame || gameOddsIsPregame || playerOddsIsPregame;
  const rawPlayerMetadata = propsData?.["player_metadata"];
  const playerMetadata: Record<string, string> = {};
  if (Array.isArray(rawPlayerMetadata)) {
    (rawPlayerMetadata as Record<string, unknown>[]).forEach((entry) => {
      const id = entry["player_id"] as string;
      const name = entry["display_name"] as string;
      if (id && name) playerMetadata[id] = name;
    });
  } else if (rawPlayerMetadata && typeof rawPlayerMetadata === "object") {
    Object.entries(rawPlayerMetadata as Record<string, unknown>).forEach(
      ([id, entry]) => {
        if (typeof entry === "string") {
          if (entry) playerMetadata[id] = entry;
        } else if (entry && typeof entry === "object") {
          const obj = entry as Record<string, unknown>;
          const name = (obj["display_name"] ?? obj["name"] ?? "") as string;
          if (name) playerMetadata[id] = name;
        }
      },
    );
  }

  // Rule hits for this game — same "triggers" shape (keyed by game id) used
  // on the active_games page, filtered here into team-level vs. player-level.
  const rawTriggers = propsData?.["triggers"] as
    | Record<string, PropTrigger[]>
    | undefined;
  const gameTriggers = (gameId && rawTriggers?.[gameId]) || [];
  const teamTriggers = gameTriggers.filter((t) => !t.player_id);
  const playerTriggers = gameTriggers.filter((t) => !!t.player_id);

  const homeTeam = propsData?.home_team ?? propsData?.event?.home_team ?? "";
  const awayTeam = propsData?.away_team ?? propsData?.event?.away_team ?? "";
  const sport = propsData?.sport ?? propsData?.event?.sport ?? "";
  const eventSport = (propsData?.event?.sport ?? "").toLowerCase();
  const isMls = eventSport === "mls";
  const isSoccer = isMls || eventSport === "world cup";
  const isNfl = eventSport === "nfl";
  const isNhl = eventSport === "nhl";
  const isNcaaf = eventSport === "ncaaf";
  const isMlb = eventSport === "mlb";
  const gameTitle =
    homeTeam && awayTeam
      ? isSoccer
        ? `${homeTeam} vs ${awayTeam}`
        : `${awayTeam} vs ${homeTeam}`
      : "";

  const eventData = propsData?.event;
  const startTime = eventData?.start_time;
  const homeScore = eventData?.home_score;
  const awayScore = eventData?.away_score;
  const hasValidScore =
    typeof homeScore === "number" &&
    !isNaN(homeScore) &&
    typeof awayScore === "number" &&
    !isNaN(awayScore);
  const gameStatus = eventData?.game_status as
    | Record<string, string>
    | undefined;
  const periodDisplay = gameStatus?.["period_display"] ?? "";
  const timer = gameStatus?.["timer"] ?? "";

  const formattedStartTime = startTime
    ? new Date(startTime).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : null;

  return (
    <div>
      <PageNav />
      <div
        style={{ padding: "0 10px", maxWidth: 600, margin: "80px auto 40px" }}
      >
        {/* <button
          className="font_family_base"
          onClick={() => navigate(`/game/${gameId}`)}
          style={{
            background: "none",
            border: "none",
            color: "#4ade80",
            cursor: "pointer",
            fontSize: 14,
            padding: 0,
            marginBottom: 4,
          }}
        >
          ← Back to Game
        </button> */}

        {gameTitle && (
          <h2
            className="font_family_base text_green"
            style={{
              fontSize: 24,
              fontWeight: "bold",
              marginBottom: 4,
              marginTop: 8,
              textAlign: "center",
            }}
          >
            {gameTitle}
          </h2>
        )}
        {sport && (
          <p
            className="font_family_base"
            style={{
              color: "#9ca3af",
              fontSize: 14,
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            {sport}
          </p>
        )}

        {gameNotStartedYet ||
        gameStatus?.["short"] === "NS" ||
        gameStatus?.["period_display"] === "NS" ? (
          <p
            className="font_family_base"
            style={{
              color: "#d1d5db",
              fontSize: 15,
              marginBottom: 20,
              textAlign: "center",
            }}
          >
            {formattedStartTime}
          </p>
        ) : hasValidScore ? (
          <p
            className="font_family_base"
            style={{
              fontSize: 22,
              fontWeight: "bold",
              marginBottom: 20,
              textAlign: "center",
              color: "#fff",
            }}
          >
            {isSoccer ? homeScore : awayScore} –{" "}
            {isSoccer ? awayScore : homeScore}
            {(timer || periodDisplay) && (
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#9ca3af",
                  marginLeft: 10,
                }}
              >
                {timer} {periodDisplay}
              </span>
            )}
          </p>
        ) : (
          <p
            className="font_family_base"
            style={{
              color: "#d1d5db",
              fontSize: 15,
              marginBottom: 20,
              textAlign: "center",
            }}
          >
            {isLoading ? "Loading" : "Starting soon"}
          </p>
        )}

        {showingPregameOdds && (
          <p
            className="font_family_base"
            style={{
              color: "#9ca3af",
              fontSize: 12,
              marginTop: -12,
              marginBottom: 16,
              textAlign: "center",
            }}
          >
            Showing pre-game odds — live odds will appear once available.
          </p>
        )}

        {isLoading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div
              className="loading-overlay-spinner"
              style={{ margin: "0 auto" }}
            />
          </div>
        )}

        {error && (
          <div
            className="font_family_base"
            style={{
              color: "#f87171",
              border: "1px solid #f87171",
              borderRadius: 8,
              padding: 16,
              textAlign: "center",
              marginTop: 20,
            }}
          >
            {error}
          </div>
        )}

        {!isLoading && propsData && (
          <>
            {/* ── Main Odds ──────────────────────────────────────────── */}
            <CollapsibleSection title="Main Odds">
              <MainOddsSection
                oddsMap={mainOdds}
                isSoccer={isSoccer}
                emptyMessage="No main odds available."
                onChartClick={(key) => navigate(`/game/${gameId}?chart=${key}`)}
              />
            </CollapsibleSection>

            {/* ── Game Odds ──────────────────────────────────────────── */}
            <CollapsibleSection
              title="Game Odds"
              highlighted={teamTriggers.length > 0}
            >
              <OddsMapSection
                oddsMap={gameOdds}
                emptyMessage="No game odds available."
                excludeTypes={
                  isSoccer
                    ? new Set(["reg_line", "win_to_nil"])
                    : isMlb || isNfl || isNcaaf
                      ? new Set(["spread", "line"])
                      : undefined
                }
                onChartClick={(key) =>
                  navigate(
                    `/game/${gameId}/props/chart?chart=${encodeURIComponent(key)}&type=game`,
                  )
                }
                triggers={teamTriggers}
                mergeTypes={isMlb ? MLB_MERGE_TYPES : undefined}
                labelOverrides={
                  isMls
                    ? MLS_LABEL_OVERRIDES
                    : isNfl || isNcaaf
                      ? FOOTBALL_LABEL_OVERRIDES
                      : undefined
                }
                memberResolvers={
                  isNfl || isNcaaf ? FOOTBALL_MEMBER_RESOLVERS : undefined
                }
              />
            </CollapsibleSection>

            {/* ── Player Odds ────────────────────────────────────────── */}
            <CollapsibleSection
              title="Player Odds"
              highlighted={playerTriggers.length > 0}
            >
              <PlayerOddsSection
                oddsMap={playerOdds}
                emptyMessage="No player odds available."
                playerMetadata={playerMetadata}
                onChartClick={(key) =>
                  navigate(
                    `/game/${gameId}/props/chart?chart=${encodeURIComponent(key)}&type=player`,
                  )
                }
                triggers={playerTriggers}
                isNfl={isNfl}
                isNhl={isNhl}
                isMls={isMls}
                isNcaaf={isNcaaf}
              />
            </CollapsibleSection>

            {/* ── Raw API Response ───────────────────────────────────── */}
            {rawResponse && (
              <div style={{ marginTop: 32 }}>
                <p
                  className="font_family_base"
                  style={{ color: "#9ca3af", fontSize: 13, marginBottom: 8 }}
                >
                  Raw API Response
                </p>
                <pre
                  className="font_family_base"
                  style={{
                    background: "#111",
                    border: "1px solid #333",
                    borderRadius: 8,
                    padding: 16,
                    fontSize: 11,
                    color: "#d1d5db",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {JSON.stringify(rawResponse, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
